import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { css, keyframes } from "styled-components";

import CheckIcon from "../../../../icons/Check";
import { AI_BUILD_STEPS } from "../../../../services/api";

const GREEN = "var(--color-brand-plus)";
const MARK = 36;
/** Each line of what the step running is doing stays this long. */
const LIVE_SECONDS = 3;

/* The arc going round the number of the step running */
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/* A new line of the step running comes in */
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

type StepState = "done" | "now" | "next";

/* The part of the line under the step running, filling up again and again */
const fill = keyframes`
  from { transform: scaleY(0); }
  to { transform: scaleY(1); }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem 1.25rem;
  background: radial-gradient(120% 120% at 0% 0%, rgba(87, 242, 139, 0.1), transparent 55%), var(--color-neutral-dark);
  border-radius: 1rem;

  &,
  & * {
    font-family: Inter, sans-serif;
  }

  /* "Etapa 2 de 4" on the title's line (the baseline of the block's first line, the title) */
  .top {
    display: flex;
    gap: 1rem;
    align-items: baseline;
    justify-content: space-between;
  }

  h3 {
    font-size: 1.125rem;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: var(--color-neutral-white);
  }

  .lead {
    margin-top: 0.25rem;
    font-size: 0.8125rem;
    color: var(--color-neutral-150);
  }

  .count {
    flex-shrink: 0;
    font-size: 0.75rem;
    font-weight: 700;
    color: ${GREEN};
    font-variant-numeric: tabular-nums;
  }

  .clock {
    font-weight: 600;
    color: var(--color-neutral-150);
  }

  ol {
    display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0;
    list-style: none;
  }
`;

/* A step on the timeline: its number (a check once done), the line down to the next one, its texts */
const Step = styled.li<{ $state: StepState; $last: boolean }>`
  position: relative;
  display: flex;
  gap: 1rem;
  padding-bottom: ${({ $last }) => ($last ? 0 : "1.75rem")};

  ${({ $last, $state }) =>
    !$last &&
    css`
      &::before,
      &::after {
        position: absolute;
        top: ${MARK}px;
        bottom: 0;
        left: ${MARK / 2 - 1}px;
        width: 2px;
        content: "";
      }

      &::before {
        background: ${$state === "done" ? GREEN : "var(--color-neutral-base)"};
      }

      ${$state === "now" &&
      css`
        &::after {
          background: ${GREEN};
          transform-origin: top;
          animation: ${fill} 2.4s ease-in-out infinite;

          @media (prefers-reduced-motion: reduce) {
            animation: none;
            transform: scaleY(0.5);
          }
        }
      `}
    `}

  .number {
    position: relative;
    z-index: 1;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: ${MARK}px;
    height: ${MARK}px;
    font-size: 0.875rem;
    font-weight: 800;
    color: ${({ $state }) => ($state === "now" ? GREEN : "var(--color-neutral-150)")};
    background: ${({ $state }) => ($state === "done" ? GREEN : "var(--color-neutral-darkness)")};
    border: 2px solid ${({ $state }) => ($state === "next" ? "var(--color-neutral-base)" : GREEN)};
    border-radius: 50%;

    svg {
      width: 14px;
      height: 12px;
    }

    /* the step running: an arc going round it */
    ${({ $state }) =>
      $state === "now" &&
      css`
        &::before {
          position: absolute;
          inset: -6px;
          content: "";
          border: 2px solid transparent;
          border-top-color: ${GREEN};
          border-right-color: ${GREEN};
          border-radius: 50%;
          animation: ${spin} 1.1s linear infinite;

          @media (prefers-reduced-motion: reduce) {
            animation: none;
          }
        }
      `}
  }

  .live {
    color: var(--color-neutral-light);
    animation: ${fadeIn} 0.4s ease;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding-top: 0.375rem;
  }

  .title {
    display: flex;
    gap: 0.625rem;
    align-items: center;
    font-size: 0.9375rem;
    font-weight: 700;
    color: ${({ $state }) => ($state === "next" ? "var(--color-neutral-150)" : "var(--color-neutral-white)")};
  }

  .meta {
    font-size: 0.75rem;
    color: var(--color-neutral-150);
  }
`;

type Props = {
  /** The step running now (0-based, as AI_BUILD_STEPS); their count: all done. */
  current: number;
};

/** While the AI builds the Keepo: a vertical timeline of its steps, done (a check), running (its line filling) or next. */
export const BuildProgress = ({ current }: Props) => {
  const { t } = useTranslation();
  const prefix = "pages.editor.aiBuild";
  const total = AI_BUILD_STEPS.length;
  const running = AI_BUILD_STEPS[current];
  const lines = running ? t(`${prefix}.stepsLive.${running}`, { returnObjects: true }) : [];
  // a missing translation comes back as a string: no lines then, the step's fixed line shows
  const live = Array.isArray(lines) ? (lines as string[]) : [];
  // seconds on this screen (the API doesn't tell when the build started), and the line of the step running
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setSeconds(value => value + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  const tick = Math.floor(seconds / LIVE_SECONDS);
  const clock = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

  return (
    <Card>
      {/* heard only when the step changes (it holds no clock or line that changes every few seconds) */}
      <span className="sr-only" role="status">
        {running
          ? `${t(`${prefix}.loading.count`, { step: current + 1, total })}: ${t(`${prefix}.steps.${running}`)}`
          : ""}
      </span>
      <div className="top">
        <div>
          <h3>{t(`${prefix}.loading.title`)}</h3>
          <p className="lead">{t(`${prefix}.loading.text`)}</p>
        </div>
        <span className="count">
          {t(`${prefix}.loading.count`, { step: Math.min(current + 1, total), total })}
          <span className="clock" aria-hidden="true">
            {" "}
            · {clock}
          </span>
        </span>
      </div>
      <ol>
        {AI_BUILD_STEPS.map((step, at) => {
          const state: StepState = at < current ? "done" : at === current ? "now" : "next";
          return (
            <Step
              key={step}
              $state={state}
              $last={at === total - 1}
              className={state}
              aria-current={state === "now" ? "step" : undefined}>
              <span className="number" aria-hidden="true">
                {state === "done" ? <CheckIcon color="var(--color-neutral-darkness)" /> : at + 1}
              </span>
              <span className="text">
                <span className="title">{t(`${prefix}.steps.${step}`)}</span>
                {/* the step running says what it's doing now, a line after another */}
                {state === "now" && live.length ? (
                  <span key={tick} className="meta live">
                    {live[tick % live.length]}
                  </span>
                ) : (
                  <span className="meta">{t(`${prefix}.stepsText.${step}`)}</span>
                )}
              </span>
            </Step>
          );
        })}
      </ol>
    </Card>
  );
};
